import React, { useState, useEffect } from "react";
import { Loader, Table, Icon, Button, Label } from "semantic-ui-react";
import { map, size, sumBy, filter, groupBy } from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import { HeaderPage, PrintHeader, PrintTable } from "../../components/Admin";
import { useAuth, usePrintOrientation, useSiteConfig } from "../../hooks";
import { getBusinessDaysAgo, getBusinessToday } from "../../utils/date";
import { getTipSettlementsApi } from "../../api/tips";
import { positionText, tipModelText } from "../../utils/tips";
import { printTipsReport } from "../../utils/tipsReport";

const money = (value) => Number(value || 0).toFixed(2);
const when = (value) => moment.parseZone(value).format("DD/MM/YY HH:mm");

// Caja → Liquidación de propinas: todas las liquidaciones por día operativo,
// con el total por persona (para planilla y contador), reimpresión de cada una
// (con firma) y descarga a Excel. Las anuladas se muestran pero no suman.
export function TipSettlementsReport() {
  const { auth } = useAuth();
  const { siteConfig, getSiteConfig } = useSiteConfig();
  const [dateFrom, setDateFrom] = useState(() => getBusinessDaysAgo(7));
  const [dateTo, setDateTo] = useState(getBusinessToday);
  const [rows, setRows] = useState(null);
  usePrintOrientation("landscape");

  useEffect(() => {
    getSiteConfig();
  }, []);

  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    let current = true;
    setRows(null);
    getTipSettlementsApi({ date_from: dateFrom, date_to: dateTo }, auth?.token)
      .then((result) => current && setRows(result))
      .catch((error) => {
        toast.error(error.message);
        if (current) setRows([]);
      });
    return () => {
      current = false;
    };
  }, [dateFrom, dateTo]);

  const valid = filter(rows, (row) => !row.voided_at);
  const bags = {
    cash: sumBy(valid, (r) => Number(r.cash_gross)),
    card: sumBy(valid, (r) => Number(r.card_gross)),
    fee: sumBy(valid, (r) => Number(r.card_fee)),
    retention: sumBy(valid, (r) => Number(r.retention_cash) + Number(r.retention_card)),
  };
  // Estado de la parte de tarjeta de una persona en una liquidación.
  const cardStatus = (row, share) =>
    Number(share.card_amount) <= 0
      ? ""
      : row.card_payout === "DRAWER"
      ? "Pagada desde la caja"
      : share.card_paid_at
      ? "Pagada"
      : "Por pagar";

  // Total por persona (liquidaciones vigentes), con lo que queda por pagar de tarjeta.
  const allShares = [];
  valid.forEach((row) => row.shares.forEach((share) => allShares.push({ row, share })));
  const people = map(
    groupBy(allShares, ({ share }) => (share.user ? `u${share.user}` : `n${share.name}`)),
    (items) => ({
      name: items[0].share.name,
      position: items[0].share.position,
      cash: sumBy(items, ({ share }) => Number(share.cash_amount)),
      card: sumBy(items, ({ share }) => Number(share.card_amount)),
      pending: sumBy(items, ({ row, share }) =>
        cardStatus(row, share) === "Por pagar" ? Number(share.card_amount) : 0
      ),
    })
  ).sort((a, b) => a.name.localeCompare(b.name));
  const rangeLabel = `Del ${moment(dateFrom).format("DD/MM/YYYY")} al ${moment(dateTo).format(
    "DD/MM/YYYY"
  )}`;

  const onPrint = (row) =>
    printTipsReport({
      siteName: siteConfig?.site_name,
      session: { opened_at: row.session_opened_at, opened_by_name: row.session_opened_by_name },
      settlement: row,
    });

  // CSV con BOM para que Excel respete las tildes.
  const download = (name, header, lines) => {
    const csv = [header, ...lines]
      .map((cols) => cols.map((col) => `"${String(col ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\r\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name}_${dateFrom}_${dateTo}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const exportTotals = () =>
    download(
      "propinas_por_persona",
      ["Persona", "Puesto", "Propina efectivo", "Propina tarjeta", "Total", "Tarjeta por pagar"],
      map(people, (p) => [
        p.name, positionText(p.position), money(p.cash), money(p.card), money(p.cash + p.card),
        money(p.pending),
      ])
    );

  const exportDetail = () =>
    download(
      "propinas_detalle",
      [
        "Liquidada", "Caja abierta", "Regla", "Persona", "Puesto", "Efectivo", "Tarjeta", "Total",
        "Tarjeta: estado", "Anulada",
      ],
      (rows || []).flatMap((row) =>
        map(row.shares, (share) => [
          when(row.created_at), when(row.session_opened_at), tipModelText(row.model), share.name,
          positionText(share.position), money(share.cash_amount), money(share.card_amount),
          money(Number(share.cash_amount) + Number(share.card_amount)), cardStatus(row, share),
          row.voided_at ? `Sí: ${row.void_reason}` : "No",
        ])
      )
    );

  return (
    <>
      <div className="screen-only">
        <HeaderPage
          title="Caja — Liquidación de propinas"
          btnTitle={
            <>
              <Icon name="print" /> Imprimir
            </>
          }
          btnClick={() => window.print()}
        />
      </div>

      <div className="screen-only" style={{ display: "flex", gap: 15, flexWrap: "wrap", margin: "10px 0 20px" }}>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Desde</label>
          <input type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Hasta</label>
          <input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div style={{ alignSelf: "flex-end", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button basic onClick={exportTotals} disabled={size(people) === 0}>
            <Icon name="file excel outline" /> Total por persona (Excel)
          </Button>
          <Button basic onClick={exportDetail} disabled={size(rows) === 0}>
            <Icon name="file excel outline" /> Detalle (Excel)
          </Button>
        </div>
      </div>

      <PrintTable>
        <PrintHeader title="Liquidación de propinas" subtitle={rangeLabel} />

        {rows === null ? (
          <Loader active inline="centered">
            Cargando...
          </Loader>
        ) : size(rows) === 0 ? (
          <p className="print-content">No hay liquidaciones de propinas en este rango.</p>
        ) : (
          <div className="print-content">
            <h4>Total por persona</h4>
            <Table compact unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Persona</Table.HeaderCell>
                  <Table.HeaderCell>Puesto</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Efectivo (S/)</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Tarjeta (S/)</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Total (S/)</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Tarjeta por pagar (S/)</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {map(people, (p) => (
                  <Table.Row key={`${p.name}-${p.position}`}>
                    <Table.Cell>{p.name}</Table.Cell>
                    <Table.Cell>{positionText(p.position)}</Table.Cell>
                    <Table.Cell textAlign="right">{money(p.cash)}</Table.Cell>
                    <Table.Cell textAlign="right">{money(p.card)}</Table.Cell>
                    <Table.Cell textAlign="right">
                      <strong>{money(p.cash + p.card)}</strong>
                    </Table.Cell>
                    <Table.Cell textAlign="right">{p.pending > 0 ? money(p.pending) : "—"}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
              <Table.Footer>
                <Table.Row>
                  <Table.HeaderCell colSpan="2">
                    {size(valid)} liquidación(es) vigente(s) · recaudado S/ {money(bags.cash)} en
                    efectivo y S/ {money(bags.card)} en tarjeta
                    {bags.fee > 0 && ` · comisión −S/ ${money(bags.fee)}`}
                    {bags.retention > 0 && ` · retención −S/ ${money(bags.retention)}`}
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">{money(sumBy(people, "cash"))}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">{money(sumBy(people, "card"))}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">
                    {money(sumBy(people, (p) => p.cash + p.card))}
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">{money(sumBy(people, "pending"))}</Table.HeaderCell>
                </Table.Row>
              </Table.Footer>
            </Table>

            <h4 style={{ marginTop: 24 }}>Liquidaciones</h4>
            <Table compact unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Liquidada</Table.HeaderCell>
                  <Table.HeaderCell>Responsable</Table.HeaderCell>
                  <Table.HeaderCell>Regla</Table.HeaderCell>
                  <Table.HeaderCell>Reparto</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Total (S/)</Table.HeaderCell>
                  <Table.HeaderCell className="screen-only" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {map(rows, (row) => (
                  <Table.Row key={row.id} negative={!!row.voided_at}>
                    <Table.Cell verticalAlign="top">
                      {when(row.created_at)}
                      {row.voided_at && (
                        <div>
                          <Label size="mini" color="red">
                            Anulada
                          </Label>
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell verticalAlign="top">{row.created_by_name}</Table.Cell>
                    <Table.Cell verticalAlign="top">{tipModelText(row.model)}</Table.Cell>
                    <Table.Cell verticalAlign="top">
                      {/* Nombre y monto en columnas, con los montos alineados a la derecha. */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto auto auto",
                          columnGap: 18,
                          justifyContent: "start",
                        }}
                      >
                        {map(row.shares, (share) => (
                          <React.Fragment key={share.id}>
                            <span>{share.name}</span>
                            <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                              S/ {money(Number(share.cash_amount) + Number(share.card_amount))}
                            </span>
                            <span style={{ color: "#c0392b" }}>
                              {cardStatus(row, share) === "Por pagar" ? "tarjeta por pagar" : ""}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                      {row.voided_at && (
                        <div style={{ color: "#777" }}>Anulada: {row.void_reason}</div>
                      )}
                    </Table.Cell>
                    <Table.Cell verticalAlign="top" textAlign="right">
                      {money(sumBy(row.shares, (s) => Number(s.cash_amount) + Number(s.card_amount)))}
                    </Table.Cell>
                    <Table.Cell verticalAlign="top" className="screen-only" textAlign="right">
                      <Button basic size="mini" onClick={() => onPrint(row)} title="Imprimir la liquidación">
                        <Icon name="print" /> Imprimir
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </PrintTable>
    </>
  );
}
